<template>
  <f7-block>
    <div class="card card-outline">
      <div class="card-header">
        {{ headerTitle }}
      </div>

      <div class="card-content card-content-padding">
        <div
          class="content-title-select"
          @click="checkAll = !checkAll"
        >
          <div class="content-title-select__input">
            <label class="checkbox">
              <input
                v-model="checkAll"
                type="checkbox"
                disabled
              >
              <i class="icon-checkbox" />
            </label>
            {{ $t('common.allFemPl').toUpperCase() }}
          </div>

          <div class="content-title-select__total">
            <f7-badge>
              {{ cardList.length }}
            </f7-badge>
          </div>
        </div>

        <div>
          <UCPreload
            :number="1"
            :show="showPreload"
          />

          <div
            v-for="a in cardList"
            :key="a.Id"
            class="content-list-checkbox"
          >
            <div
              class="content-list-checkbox-item"
              :class="{ checked: a.checked === true }"
            >
              <label class="checkbox">
                <input
                  :checked="a.checked"
                  type="checkbox"
                  @change="changeSelected(a)"
                >
                <i class="icon-checkbox" />
              </label>

              <div
                class="content-list-checkbox-item__details"
                @click="changeSelected(a)"
              >
                {{ HiStringSupport.TextMaximumLength(getCardTitle(a), Number(maximumTextLength)) }}
                <div class="content-list-checkbox-item__details--total ">
                  <f7-badge>
                    {{ getBadgeTitleValue(a) }}
                  </f7-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </f7-block>
</template>

<script setup>
// @ts-check
import UCPreload from '@/components/common/UCPreload.vue';
import { HiStringSupport } from '@/js/common/HIConversionSupport';
import { computed } from 'vue';

/** @import { PropType } from 'vue' */
/** @typedef {Record<string, any>} EntitySelectionItem */

const props = defineProps({
  headerTitle: {
    type: String,
    default: 'Header',
  },
  cardTitleProp: {
    type: /** @type {PropType<string[]>} */ (Array),
    default: () => [],
  },
  entityList: {
    type: /** @type {PropType<EntitySelectionItem[]>} */ (Array),
    default: () => [],
  },
  showPreload: {
    type: Boolean,
    default: false,
  },
  separationChar: {
    type: String,
    default: ' - ',
  },
  maximumTextLength: {
    type: String,
    default: '20',
  },
});

const emit = defineEmits([
  'checkedAll',
  'changeSelected',
]);

const checkAll = computed({
  get() {
    return props.entityList.length > 0 && props.entityList.every((item) => item.checked);
  },
  set(value) {
    props.entityList.forEach((item) => {
      item.checked = value;
    });
    emit('checkedAll', value);
  },
});

const cardList = computed(() => filterCard(props.entityList));

/**
 * @param {EntitySelectionItem} item
 * @returns {void}
 */
function changeSelected(item) {
  item.checked = !item.checked;
  emit('changeSelected', item);
}

/**
 * @param {EntitySelectionItem} item
 * @returns {string}
 */
function getCardTitle(item) {
  let title = '';

  if (props.cardTitleProp.length > 1) {
    props.cardTitleProp.forEach((element) => {
      if (element.includes('.')) {
        const [mainProp, subProp] = element.split('.');
        if (mainProp && subProp) {
          title += item[mainProp][subProp] + props.separationChar;
        }
      } else {
        title += item[item[element]] + props.separationChar;
      }
    });
    title = title.substring(0, title.length - 1);
  } else if (props.cardTitleProp[0]) {
    title = item[props.cardTitleProp[0]];
  }

  return title;
}

/**
 * @param {EntitySelectionItem[]} entityList
 * @returns {EntitySelectionItem[]}
 */
function filterCard(entityList) {
  /** @type {EntitySelectionItem[]} */
  const result = [];

  entityList.forEach((item) => {
    if (item != null) {
      if ('Id' in item) {
        const lista = result.find((element) => element.Id === item.Id);
        if (!lista) {
          result.push(item);
        }
      } else {
        const lista = result.find((element) => element.Codigo === item.Codigo);
        if (!lista) {
          result.push(item);
        }
      }
    }
  });

  return result;
}

/**
 * @param {EntitySelectionItem} value
 * @returns {number}
 */
function getBadgeTitleValue(value) {
  let cont = 0;
  if ('Id' in value) {
    props.entityList.forEach((element) => {
      if (element.Id === value.Id) {
        cont += 1;
      }
    });
  } else {
    props.entityList.forEach((element) => {
      if (element.Codigo === value.Codigo) {
        cont += 1;
      }
    });
  }
  return cont;
}
</script>

<style scoped lang="scss">
.content-list-checkbox-item,
.content-title-select {
  padding: 7px 15px;
  background: #f4f5f7;
  margin-bottom: 2px;
  border: 1px solid #ebebed;
  border-radius: 3px;

  &.incidence {
    background: rgb(222 53 11 / 10%);
    border: 1px solid #ff5630;
  }

  &.out-of-line {
    background: rgb(222 53 11 / 10%);
    border: 1px solid #fcff30;
  }
}

.content-title-select {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__input {
    display: flex;
    align-items: center;
    color: var(--his-steel-blue);
    font-weight: 400;
    font-size: 15px;
    text-transform: uppercase;

    label {
      margin-right: 12px;
    }
  }
}

.card {
  margin: 0;

  .card-header {
    background-color: #ebecf0;
    text-transform: uppercase;
    color: #253858;
    font-weight: 400;
  }
}

.badge {
  height: 22px;
  width: 22px;
  font-size: 15px;
  background: #e4e4e4;
  color: var(--his-metal);
  font-weight: 500;
}

.content-list-checkbox-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;

  > div {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }

  label {
    margin-right: 10px;
  }

  &__details {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 16px;

    &--title {
      color: var(--his-dark-blue);
    }

    &--subtitle {
      color: var(--his-kiwi-pulp);
      text-transform: uppercase;
    }

    img {
      border-radius: 50%;
      margin: 0 10px 0 0;
      width: 64px;
      height: 64px;
    }

    &--total {
      display: flex;
      width: 15px;
      height: 20px;
      padding: 10px;

      img {
        height: 20px;
        width: 20px;
        margin: 0;
        padding: 0 0 0 5px !important;
        border-radius: 0;
      }
    }
  }

  &__warning {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  &__time {
    color: var(--his-slate-blue);
    font-size: 28px;
    display: flex;
    align-items: center;

    &::after {
      font-family: "framework7-core-icons";
      font-weight: normal;
      direction: ltr;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
      -moz-osx-font-smoothing: grayscale;
      -moz-font-feature-settings: "liga";
      font-feature-settings: "liga";
      text-align: center;
      display: block;
      margin-left: 15px;
      font-size: var(--f7-list-chevron-icon-font-size);
      color: var(--f7-list-chevron-icon-color);
      pointer-events: none;
      right:
        calc(
          var(--f7-list-item-padding-horizontal) + var(--f7-safe-area-right)
        );
      content: var(--f7-list-chevron-icon-right);
    }
  }

  &.checked {
    background-color: var(--his-steel-blue);
    color: #fff;

    .badge {
      background-color: #253858;
      color: #fff;
    }
  }
}

label.item-checkbox input[type="checkbox"]:checked ~ .icon-checkbox,
label.item-checkbox input[type="checkbox"]:checked ~ * .icon-checkbox,
.checkbox input[type="checkbox"]:checked ~ i,
label.item-checkbox input[type="checkbox"]:indeterminate ~ .icon-checkbox,
label.item-checkbox input[type="checkbox"]:indeterminate ~ * .icon-checkbox,
.checkbox input[type="checkbox"]:indeterminate ~ i {
  background-color: #566784;
  border-color: #566784;
  zoom: 1.3;
}

.icon-checkbox {
  zoom: 1.3;
}

@media (max-width: 530px) {
  .content-list-checkbox-item__details {
    font-size: 14px !important;
    font-weight: 600 !important;
    width: 48px !important;
    height: 48px !important;
  }

  .content-list-checkbox-item__time {
    font-size: 18px !important;
    font-weight: 600;
    padding-left: 10px;
  }
}

.content-list-checkbox-scroll {
  height: 72vh;
  overflow-y: auto;
}
</style>
